import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from "@mui/material";

const money = (value) => {
  const n = Number(value || 0);
  return `$${n.toFixed(2)}`;
};

const parseDate = (raw) => {
  if (!raw) return null;

  const value = String(raw);

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }

  return new Date(value);
};

const getCurrentMonthValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const getMonthOptions = () => {
  const now = new Date();
  const options = [];

  for (let i = 0; i <= 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const label = date.toLocaleDateString("es-EC", {
      month: "long",
      year: "numeric",
    });

    options.push({
      value,
      label: label.charAt(0).toUpperCase() + label.slice(1),
    });
  }

  return options;
};

const formatLabel = (item, periodo) => {
  const raw = item.fecha || item.mes;
  const date = parseDate(raw);

  if (!date || Number.isNaN(date.getTime())) {
    return String(raw || "");
  }

  if (periodo === "anio") {
    return date.toLocaleDateString("es-EC", {
      month: "short",
    });
  }

  return date.toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
  });
};

const formatTooltipDate = (item, periodo) => {
  const raw = item.fecha || item.mes;
  const date = parseDate(raw);

  if (!date || Number.isNaN(date.getTime())) {
    return String(raw || "");
  }

  if (periodo === "anio") {
    return date.toLocaleDateString("es-EC", {
      month: "long",
      year: "numeric",
    });
  }

  return date.toLocaleDateString("es-EC", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getTitle = (periodo, selectedMonth) => {
  if (periodo === "semana") return "Ventas de la semana";

  if (periodo === "mes") {
    const [year, month] = selectedMonth.split("-");
    const date = new Date(Number(year), Number(month) - 1, 1);
    const label = date.toLocaleDateString("es-EC", {
      month: "long",
      year: "numeric",
    });

    return `Ventas de ${label.charAt(0).toUpperCase() + label.slice(1)}`;
  }

  return "Ventas del año";
};

const getLabelStep = (length, periodo) => {
  if (periodo === "semana") return 1;
  if (periodo === "anio") return 1;
  if (length <= 10) return 1;
  if (length <= 20) return 2;
  return 3;
};

const SalesChartPanel = ({ accessToken }) => {
  const [periodo, setPeriodo] = useState("semana");
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthValue());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(null);

  const monthOptions = useMemo(() => getMonthOptions(), []);

  useEffect(() => {
    const loadData = async () => {
      if (!accessToken) return;

      setLoading(true);

      try {
        const url =
          periodo === "mes"
            ? `/dashboard/ventas/?periodo=mes&mes=${selectedMonth}`
            : `/dashboard/ventas/?periodo=${periodo}`;

        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await res.json();

        if (res.ok && Array.isArray(data)) {
          setItems(data);
        } else {
          setItems([]);
        }
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [periodo, selectedMonth, accessToken]);

  const total = useMemo(() => {
    return items.reduce((acc, item) => acc + Number(item.neto || 0), 0);
  }, [items]);

  const chart = useMemo(() => {
    const values = items.map((item) => Number(item.neto || 0));
    const max = Math.max(...values, 1);
    const width = 720;
    const height = 260;
    const paddingX = 44;
    const paddingY = 28;
    const innerWidth = width - paddingX * 2;
    const innerHeight = height - paddingY * 2;

    const points = values.map((value, index) => {
      const x =
        values.length === 1
          ? width / 2
          : paddingX + (index * innerWidth) / (values.length - 1);

      const y = paddingY + innerHeight - (value / max) * innerHeight;

      return { x, y, value };
    });

    const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");

    const area =
      points.length > 0
        ? `${points.map((p) => `${p.x},${p.y}`).join(" ")} ${points[points.length - 1].x},${height - paddingY} ${points[0].x},${height - paddingY}`
        : "";

    return {
      width,
      height,
      paddingX,
      paddingY,
      innerHeight,
      max,
      points,
      polyline,
      area,
      labelStep: getLabelStep(values.length, periodo),
    };
  }, [items, periodo]);

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        bgcolor: "#fff",
        border: "1px solid #e5eaf2",
        boxShadow: "0 8px 26px rgba(15, 23, 42, 0.06)",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, color: "#152238", fontSize: 18 }}>
            {getTitle(periodo, selectedMonth)}
          </Typography>

          <Typography sx={{ color: "#64748b", fontSize: 13 }}>
            Total del período: {money(total)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 145 } }}>
            <Select
              value={periodo}
              onChange={(e) => {
                setPeriodo(e.target.value);
                setHovered(null);
              }}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              <MenuItem value="semana">Semana</MenuItem>
              <MenuItem value="mes">Mes</MenuItem>
              <MenuItem value="anio">Año</MenuItem>
            </Select>
          </FormControl>

          {periodo === "mes" && (
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
              <Select
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setHovered(null);
                }}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {monthOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            height: 310,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={30} />
        </Box>
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Box sx={{ minWidth: 620, position: "relative" }}>
            <svg
              viewBox={`0 0 ${chart.width} ${chart.height}`}
              style={{ width: "100%", height: 300, display: "block" }}
              onMouseLeave={() => setHovered(null)}
            >
              {[0, 1, 2, 3].map((line) => {
                const y = chart.paddingY + (line * chart.innerHeight) / 3;
                const value = chart.max - (line * chart.max) / 3;

                return (
                  <g key={line}>
                    <line
                      x1={chart.paddingX}
                      x2={chart.width - chart.paddingX}
                      y1={y}
                      y2={y}
                      stroke="#e6edf7"
                      strokeWidth="1"
                    />

                    <text
                      x="4"
                      y={y + 4}
                      fontSize="11"
                      fill="#64748b"
                      fontWeight="600"
                    >
                      {money(value)}
                    </text>
                  </g>
                );
              })}

              {chart.area && (
                <polygon points={chart.area} fill="rgba(8,112,162,0.10)" />
              )}

              {chart.polyline && (
                <polyline
                  points={chart.polyline}
                  fill="none"
                  stroke="#0870A2"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {chart.points.map((p, index) => {
                const showLabel =
                  index === 0 ||
                  index === chart.points.length - 1 ||
                  index % chart.labelStep === 0;

                return (
                  <g key={index}>
                    <line
                      x1={p.x}
                      x2={p.x}
                      y1={chart.paddingY}
                      y2={chart.height - chart.paddingY}
                      stroke={hovered === index ? "#cbd5e1" : "transparent"}
                      strokeWidth="1"
                    />

                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={hovered === index ? "7" : "5"}
                      fill="#0870A2"
                      stroke="#fff"
                      strokeWidth="2"
                    />

                    <circle
                      cx={p.x}
                      cy={p.y}
                      r="18"
                      fill="transparent"
                      style={{ cursor: "pointer" }}
                      onMouseEnter={() => setHovered(index)}
                      onMouseMove={() => setHovered(index)}
                    />

                    {showLabel && (
                      <text
                        x={p.x}
                        y={chart.height - 5}
                        textAnchor="middle"
                        fontSize="11"
                        fill="#64748b"
                        fontWeight="700"
                      >
                        {formatLabel(items[index], periodo)}
                      </text>
                    )}
                  </g>
                );
              })}

              {hovered !== null && items[hovered] && chart.points[hovered] && (
                <g>
                  <rect
                    x={
                      chart.points[hovered].x > chart.width - 205
                        ? chart.points[hovered].x - 194
                        : chart.points[hovered].x + 14
                    }
                    y={
                      chart.points[hovered].y < 72
                        ? chart.points[hovered].y + 14
                        : chart.points[hovered].y - 68
                    }
                    width="180"
                    height="54"
                    rx="9"
                    fill="#0f172a"
                    opacity="0.96"
                  />

                  <text
                    x={
                      chart.points[hovered].x > chart.width - 205
                        ? chart.points[hovered].x - 181
                        : chart.points[hovered].x + 27
                    }
                    y={
                      chart.points[hovered].y < 72
                        ? chart.points[hovered].y + 35
                        : chart.points[hovered].y - 47
                    }
                    fontSize="11"
                    fill="#cbd5e1"
                    fontWeight="700"
                  >
                    {formatTooltipDate(items[hovered], periodo)}
                  </text>

                  <text
                    x={
                      chart.points[hovered].x > chart.width - 205
                        ? chart.points[hovered].x - 181
                        : chart.points[hovered].x + 27
                    }
                    y={
                      chart.points[hovered].y < 72
                        ? chart.points[hovered].y + 55
                        : chart.points[hovered].y - 27
                    }
                    fontSize="15"
                    fill="#ffffff"
                    fontWeight="900"
                  >
                    {money(items[hovered].neto)}
                  </text>
                </g>
              )}

              {items.length === 0 && (
                <text
                  x={chart.width / 2}
                  y={chart.height / 2}
                  textAnchor="middle"
                  fontSize="14"
                  fill="#64748b"
                  fontWeight="700"
                >
                  No hay datos para mostrar
                </text>
              )}
            </svg>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default SalesChartPanel;